import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { Minus, Plus, Trash2, ArrowRight } from 'lucide-react';
import { RootState } from '../../app/store';
import { addToCart, removeFromCart, clearCart } from './cartSlice';
import { orderService } from '../orders/orderService';
import { OrderError } from '../orders/typres';
import { Product, GameCard } from '../product/types';
import { productService } from '../product/productService';
import { cartService } from './cartService';
import { GetCartResponse } from './types';
import { authService } from '../auth/authService';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import ProductCarousel from '@/components/ProductCarousel';
import { hasActiveDiscount, originalPrice } from '../product/discount';

interface CartItemWithProduct {
  id: number;
  quantity: number;
  product?: Product;
}

const formatPrice = (price: number) => new Intl.NumberFormat('ru-RU').format(price);

const CartSkeleton = () => (
  <div className="min-h-screen overflow-x-hidden bg-background px-4 py-6 sm:py-8">
    <div className="mx-auto max-w-[90rem]">
      <div className="animate-pulse space-y-4">
        <div className="h-9 w-40 rounded-2xl bg-foreground/5" />
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_360px]">
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-32 rounded-[28px] bg-foreground/5" />
            ))}
          </div>
          <div className="h-64 rounded-[28px] bg-foreground/5" />
        </div>
      </div>
    </div>
  </div>
);

const CartPage = () => {
  const cart = useSelector((state: RootState) => state.cart);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [cartItems, setCartItems] = useState<CartItemWithProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [placingOrder, setPlacingOrder] = useState(false);
  const [contactRequired, setContactRequired] = useState(false);
  const [contact, setContact] = useState('');
  const [contactError, setContactError] = useState('');
  const [orderMessage, setOrderMessage] = useState<{ text: string; success: boolean } | null>(null);
  const [salesLeaders, setSalesLeaders] = useState<GameCard[]>([]);

  useEffect(() => {
    productService.fetchHome()
      .then(data => setSalesLeaders(data.salesLeaders))
      .catch(() => {});
  }, []);

  const refetchCart = async () => {
    const bucket = await cartService.get() as unknown as GetCartResponse;
    const normalized: CartItemWithProduct[] = bucket.products.map(p => ({
      id: p.id,
      quantity: p.quantity,
      product: {
        id: p.id,
        name: p.name,
        description: p.description,
        price: p.price,
        picture: p.picture,
        discountPercent: p.discountPercent,
        discountEndsAt: p.discountEndsAt,
      } as Product,
    }));
    setCartItems(normalized.filter(x => typeof x.id === 'number'));
  };

  useEffect(() => {
    setLoading(true);
    refetchCart()
      .catch(() => setCartItems([]))
      .finally(() => setLoading(false));
  }, [cart]);

  const handleAdd = async (productId: number) => {
    try {
      await cartService.add(productId);
      dispatch(addToCart(productId));
      await refetchCart();
    } catch (e) {
      console.error(e);
    }
  };

  const handleRemove = async (productId: number) => {
    try {
      await cartService.remove(productId);
      dispatch(removeFromCart(productId));
      await refetchCart();
    } catch (e) {
      console.error(e);
    }
  };

  const total = cartItems.reduce((sum, item) => sum + (item.product?.price || 0) * item.quantity, 0);

  const handlePlaceOrder = async () => {
    if (cartItems.length === 0) return;

    if (!authService.isAuthenticated()) {
      navigate('/login?returnTo=/cart');
      return;
    }

    try {
      setPlacingOrder(true);
      setOrderMessage(null);
      setContactError('');

      const response = await orderService.placeOrder(contact.trim() || undefined);
      dispatch(clearCart());
      navigate('/order-success', { state: { orderResponse: response } });
    } catch (error) {
      const orderError = error as OrderError;
      if (orderError.code === 'CONTACT_REQUIRED') {
        setContactRequired(true);
        setContactError(orderError.message);
      } else {
        setOrderMessage({ text: 'Не удалось оформить заказ. Попробуйте ещё раз.', success: false });
      }
    } finally {
      setPlacingOrder(false);
    }
  };

  if (loading) return <CartSkeleton />;

  return (
    <div className="min-h-screen overflow-x-hidden bg-background px-4 py-6 text-foreground sm:py-8">
      <div className="mx-auto max-w-[90rem]">

        {/* Header */}
        <div className="mb-6 flex items-center gap-3">
          <h1 className="text-3xl font-semibold sm:text-5xl">Корзина</h1>
          {cartItems.length > 0 && (
            <span className="rounded-full bg-foreground/10 px-3 py-0.5 text-sm text-foreground/60">
              {cartItems.length}
            </span>
          )}
        </div>

        {/* Order message */}
        {orderMessage && (
          <div className={`mb-6 flex items-center gap-3 rounded-[20px] border p-4 ${
            orderMessage.success
              ? 'border-green-500/30 bg-green-500/10 text-green-300'
              : 'border-red-500/30 bg-red-500/10 text-red-600 dark:text-red-300'
          }`}>
            <div className={`h-2 w-2 shrink-0 rounded-full ${orderMessage.success ? 'bg-green-400' : 'bg-red-400'}`} />
            {orderMessage.text}
          </div>
        )}

        {cartItems.length === 0 ? (
          /* Empty state */
          <div className="rounded-[32px] border border-foreground/10 bg-card py-20 text-center shadow-[0_24px_70px_rgba(0,0,0,0.35)]">
            <h2 className="mb-2 text-2xl font-semibold text-foreground">Корзина пуста</h2>
            <p className="mb-8 text-gray-400">Добавьте что-нибудь, чтобы оформить заказ</p>
            <Button asChild className="rounded-full bg-red-600 px-8 hover:bg-red-700">
              <Link to="/">Перейти в каталог</Link>
            </Button>
          </div>
        ) : (
          /* Cart layout: items + sticky summary */
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_360px] lg:items-start">

            {/* Items list */}
            <div className="min-w-0 space-y-3">
              {cartItems.map((item) => {
                const discounted = item.product ? hasActiveDiscount(item.product) : false;
                const oldPrice = item.product ? originalPrice(item.product) : null;
                return (
                <div
                  key={item.id}
                  className="grid min-w-0 grid-cols-[auto_minmax(0,1fr)] items-start gap-3 rounded-[28px] border border-foreground/10 bg-card p-3 transition-all hover:border-foreground/20 sm:flex sm:items-center sm:gap-4 sm:p-4"
                >
                  {/* Cover */}
                  {item.product?.picture && (
                    <Link to={`/product/${item.id}`} className="shrink-0">
                      <img
                        src={item.product.picture}
                        alt={item.product.name}
                        className="h-20 w-16 rounded-[16px] object-cover"
                      />
                    </Link>
                  )}

                  {/* Info */}
                  <div className={`min-w-0 flex-1 ${item.product?.picture ? '' : 'col-span-2'}`}>
                    <Link to={`/product/${item.id}`}>
                      <h3 className="break-words text-base font-semibold text-foreground hover:text-foreground/80 sm:truncate">
                        {item.product?.name || `Товар ${item.id}`}
                      </h3>
                    </Link>
                    <div className="mt-1 flex flex-wrap items-center gap-2">
                      <p className="text-lg font-semibold text-red-600 dark:text-red-400">
                        {formatPrice(item.product?.price || 0)} ₽
                      </p>
                      {oldPrice && (
                        <span className="text-sm text-foreground/40 line-through decoration-red-500/70">
                          {formatPrice(oldPrice)} ₽
                        </span>
                      )}
                      {discounted && (
                        <span className="rounded-full bg-red-500/15 px-2 py-1 text-[11px] font-bold text-red-600 dark:text-red-400">
                          −{item.product?.discountPercent}%
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Quantity controls + remove */}
                  <div className="col-span-2 flex min-w-0 shrink-0 items-center justify-end gap-2 border-t border-foreground/10 pt-3 sm:border-0 sm:pt-0">
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label="Уменьшить количество"
                      onClick={() => handleRemove(item.id)}
                      className="h-9 w-9 rounded-full border border-foreground/10 text-foreground/60 hover:border-foreground/20 hover:bg-foreground/5 hover:text-foreground"
                    >
                      <Minus className="h-4 w-4" />
                    </Button>

                    <span className="w-7 text-center text-base font-semibold text-foreground">
                      {item.quantity}
                    </span>

                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label="Увеличить количество"
                      onClick={() => handleAdd(item.id)}
                      className="h-9 w-9 rounded-full border border-foreground/10 text-foreground/60 hover:border-foreground/20 hover:bg-foreground/5 hover:text-foreground"
                    >
                      <Plus className="h-4 w-4" />
                    </Button>

                    <Separator orientation="vertical" className="mx-1 h-6 bg-foreground/10" />

                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label="Удалить товар из корзины"
                      onClick={() => {
                        for (let i = 0; i < item.quantity; i++) handleRemove(item.id);
                      }}
                      className="h-9 w-9 rounded-full text-foreground/30 hover:bg-red-500/10 hover:text-red-400"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                );
              })}
            </div>

            {/* Order summary (sticky on desktop) */}
            <div className="min-w-0 lg:sticky lg:top-6">
              <div className="rounded-[28px] border border-foreground/10 bg-card p-4 shadow-[0_24px_70px_rgba(0,0,0,0.35)] sm:p-6">
                <h2 className="mb-5 text-xs font-semibold uppercase tracking-[0.22em] text-foreground/40">
                  Итого
                </h2>

                <div className="space-y-3">
                  {cartItems.map(item => (
                    <div key={item.id} className="flex items-start justify-between gap-2 text-sm">
                      <span className="min-w-0 flex-1 truncate text-foreground/60">
                        {item.product?.name || `Товар ${item.id}`}
                        {item.quantity > 1 && (
                          <span className="ml-1 text-foreground/30">×{item.quantity}</span>
                        )}
                      </span>
                      <span className="shrink-0 font-medium text-foreground">
                        {formatPrice((item.product?.price || 0) * item.quantity)} ₽
                      </span>
                    </div>
                  ))}
                </div>

                <Separator className="my-5 bg-foreground/10" />

                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="text-base font-semibold text-foreground">Сумма</span>
                  <span className="text-2xl font-bold text-red-600 dark:text-red-400 sm:text-3xl">{formatPrice(total)} ₽</span>
                </div>

                <form className="mt-6 space-y-3" onSubmit={event => { event.preventDefault(); if (!placingOrder) void handlePlaceOrder(); }}>
                  {contactRequired && (
                    <div className="space-y-2">
                      <label htmlFor="order-contact" className="block text-sm font-medium">Telegram или email</label>
                      <p id="order-contact-hint" className="text-sm text-foreground/60">Укажите один контакт, чтобы мы могли связаться с вами по заказу.</p>
                      <input
                        id="order-contact"
                        type="text"
                        value={contact}
                        onChange={event => { setContact(event.target.value); setContactError(''); }}
                        placeholder="@username или name@example.com"
                        autoFocus
                        autoCapitalize="none"
                        autoCorrect="off"
                        spellCheck={false}
                        required
                        maxLength={254}
                        disabled={placingOrder}
                        aria-invalid={!!contactError}
                        aria-describedby={`order-contact-hint${contactError ? ' order-contact-error' : ''}`}
                        className="w-full min-w-0 rounded-xl border border-foreground/20 bg-background px-3 py-3 text-base text-foreground outline-none focus:border-red-500 disabled:opacity-50"
                      />
                      {contactError && <p id="order-contact-error" role="alert" className="text-sm text-red-600 dark:text-red-400">{contactError}</p>}
                    </div>
                  )}
                  <Button
                    type="submit"
                    disabled={placingOrder}
                    className="w-full rounded-[18px] bg-[linear-gradient(90deg,#991b1b,#dc2626)] py-6 text-base font-semibold hover:opacity-90 disabled:opacity-50"
                  >
                    {placingOrder ? (
                      <div className="flex items-center gap-2">
                        <div className="h-4 w-4 animate-spin rounded-full border-2 border-foreground/30 border-t-white" />
                        Оформляем...
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        Оформить заказ
                        <ArrowRight className="h-4 w-4" />
                      </div>
                    )}
                  </Button>

                  <Button
                    asChild
                    variant="ghost"
                    className="w-full rounded-[18px] border border-foreground/10 text-foreground/60 hover:border-foreground/20 hover:bg-foreground/5 hover:text-foreground"
                  >
                    <Link to="/">Продолжить покупки</Link>
                  </Button>
                </form>
              </div>
            </div>

          </div>
        )}

        {/* Sales leaders carousel */}
        {salesLeaders.length > 0 && (
          <div className="mt-14">
            <h2 className="mb-6 text-2xl font-semibold text-foreground">Лидеры продаж</h2>
            <ProductCarousel products={salesLeaders} />
          </div>
        )}
      </div>
    </div>
  );
};

export default CartPage;
